import { useQuery } from '@tanstack/react-query';
import { useShellSearchParams } from '../lib/use-shell-search-params';
import { useLocale } from '../i18n/locale-context';
import {
  fetchGovSourceCounts,
  parseSourceParam,
  parseKindParam,
  type GovSourceKey,
  parseStatusParam,
  parseIntParam,
  parseIsoDateParam,
} from '../lib/catalog-client';
import { TabStrip, type TabStripItem } from './tab-strip';

/** Exported so a test can pin this literal list to the real `GOV_SOURCE_KEYS`
 *  from catalog-client — the same guard `gov-sources.spec.ts` runs on the API
 *  against the UZ country config, so a new government platform can't filter on
 *  the API while having no option here. Order = by open-lot volume. */
export const TABS: Array<{ value: '' | GovSourceKey; labelKey: string }> = [
  { value: '', labelKey: 'catalog.source.all' },
  { value: 'tender-mc', labelKey: 'catalog.source.tender-mc' },
  { value: 'xarid-prequest', labelKey: 'catalog.source.xarid-prequest' },
  { value: 'etender-uzex', labelKey: 'catalog.source.etender-uzex' },
  { value: 'uzex-xarid', labelKey: 'catalog.source.uzex-xarid' },
  { value: 'xt-xarid', labelKey: 'catalog.source.xt-xarid' },
  { value: 'ebirja', labelKey: 'catalog.source.ebirja' },
  { value: 'cooperation-auction', labelKey: 'catalog.source.cooperation-auction' },
  { value: 'farma-uzex', labelKey: 'catalog.source.farma-uzex' },
];

export interface GovSourceFilterItem {
  value: '' | GovSourceKey;
  /** The platform's name, as the locale gives it. */
  label: string;
  /** Open lots under the caller's other filters; undefined while loading or during a keyword search. */
  count: number | undefined;
}

/**
 * Фильтр госплощадок каталога (параметр `source` в URL — всё состояние в
 * адресной строке, как у остальных фильтров): значение, пункты со счётчиками
 * и выбор. Из него рисуется выпадающий список «Площадка» в ряду фильтров
 * (`CatalogFilters`, ПК) и в листе «Фильтры» (телефон) — с 20.10 вместо
 * второй полосы вкладок над списком: владелец счёл две полосы перегрузом, а
 * список в ряду с «Регион» и «Статус» прячет фильтр до тех пор, пока он не
 * понадобится.
 *
 * Публичный: виден любому читателю госкаталога UZ — где именно, решает
 * страница (`catalog.tsx`), не этот хук. Строка источника на карточке
 * (площадка, ссылка, номер лота) остаётся за Pro в API (`source-ref.ts`), и
 * фильтр этого не меняет.
 *
 * Счётчики обязательны, а не декоративны: площадки перекошены (у tender-mc
 * больше тысячи открытых лотов, у farma-uzex — один), и без числа пункт
 * выглядит сломанным. `scope`/`status`/`awarded` приходят от страницы, как у
 * KindTabs: без scope сервер считает дефолтный каталог (gov + corporate),
 * поэтому «Все» больше суммы площадок — корпоративные порталы своего пункта
 * не имеют.
 *
 * Пока активен текстовый поиск (`q`), счётчики не запрашиваются: выдача
 * ранжируется гибридным поиском с порогом релевантности, а эндпоинт счётчиков
 * `q` игнорирует — числа бы врали. Сам фильтр при этом сужает и поиск:
 * `GET /catalog/search` принимает `source`.
 */
export function useGovSourceFilter({
  scope,
  status: statusOverride,
  awarded,
  enabled = true,
}: { scope?: string; status?: string; awarded?: boolean; enabled?: boolean } = {}) {
  const { t } = useLocale();
  const [params, setParams] = useShellSearchParams();
  const value = parseSourceParam(params.get('source')) ?? '';

  const region = params.get('region') ?? undefined;
  const category = params.get('category') ?? undefined;
  const ikpu = params.get('ikpu') ?? undefined;
  const niche = params.get('niche') ?? undefined;
  // Same as KindTabs: a route-imposed status beats the URL's.
  const status = statusOverride ?? parseStatusParam(params.get('status'));
  const q = params.get('q') ?? undefined;
  // Guarded like every other count request: an unrecognised value is "no
  // filter", never passed through to 400 the counts request.
  const minBudgetRaw = parseIntParam(params.get('minBudget'));
  const maxBudgetRaw = parseIntParam(params.get('maxBudget'));
  const deadlineFrom = parseIsoDateParam(params.get('deadlineFrom'));
  const deadlineTo = parseIsoDateParam(params.get('deadlineTo'));
  const since = parseIsoDateParam(params.get('since'));
  const kind = parseKindParam(params.get('kind'));
  const minBudget = minBudgetRaw !== undefined ? Number(minBudgetRaw) : undefined;
  const maxBudget = maxBudgetRaw !== undefined ? Number(maxBudgetRaw) : undefined;

  const { data: counts } = useQuery({
    queryKey: [
      'catalog',
      'source-counts',
      {
        region,
        category,
        ikpu,
        niche,
        status,
        scope,
        awarded,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
        kind,
      },
    ],
    queryFn: () =>
      fetchGovSourceCounts({
        region,
        category,
        ikpu,
        niche,
        status,
        scope,
        awarded,
        minBudget,
        maxBudget,
        deadlineFrom,
        deadlineTo,
        since,
        kind,
      }),
    // Точного числа при активном текстовом поиске не существует — та же логика,
    // что у IfiSourceTabs/KindTabs.
    enabled: enabled && !q,
  });

  function select(next: string) {
    const v = parseSourceParam(next) ?? '';
    const p = new URLSearchParams(params);
    if (v) p.set('source', v);
    else p.delete('source');
    p.delete('page'); // смена площадки сбрасывает пагинацию
    setParams(p);
  }

  const items: GovSourceFilterItem[] = TABS.map((tab) => ({
    value: tab.value,
    label: t(tab.labelKey),
    count: counts ? (tab.value === '' ? counts.all : counts[tab.value]) : undefined,
  }));

  return { value, items, select };
}

/**
 * Чипы госплощадок — прежний вид фильтра (полоса вкладок над списком).
 * Страница каталога с 20.10 его не рисует (см. `useGovSourceFilter`); оставлен
 * на один выкат, чтобы откат был сменой одной строки в `catalog.tsx`.
 */
export function SourceTabs({
  scope,
  status,
  awarded,
}: { scope?: string; status?: string; awarded?: boolean } = {}) {
  const { t } = useLocale();
  const { value, items, select } = useGovSourceFilter({ scope, status, awarded });
  const tabs: TabStripItem<'' | GovSourceKey>[] = items.map(({ value: v, label, count }) => ({
    value: v,
    label,
    count,
    // Цифры в TabStrip помечены aria-hidden, поэтому число — в aria-label.
    // Пока счётчики грузятся — просто метка, без фразы с дырой вместо числа.
    ariaLabel: count != null ? t('catalog.source.ariaLabel', { label, count }) : label,
  }));
  return (
    <TabStrip ariaLabel={t('catalog.source.aria')} items={tabs} active={value} onSelect={select} />
  );
}
